import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  FormControlLabel,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import useGlobalContext from "hooks/globalData";
import useListTitle from "hooks/useListTitle";
import { useState } from "react";
import { useIntl } from "react-intl";
import { useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import { RunToolRequest } from "services/KnowfullyAdminClient";

const SCRIPT_RUNNER_TOOL_KEY = "ScriptRunnerTool";

const styles = {
  script: {
    my: 2,
    "& textarea": {
      fontFamily: "monospace",
      fontSize: "0.85rem",
    },
  },
  transactionHint: {
    mb: 2,
  },
  warning: {
    mb: 2,
  },
};

// Script Runner is an ordinary data tool; this page only builds its input and hands it to the Data Tools
// endpoint. The run itself is followed on the shared Data Tools run page.
const ScriptRunnerList = () => {
  const navigate = useNavigate();
  const { formatMessage: t } = useIntl();
  const { dataToolsService, dataToolsRunUrl } = useGlobalContext();
  useListTitle("scriptRunner");

  const [target, setTarget] = useState("");
  const [script, setScript] = useState("");
  const [useTransaction, setUseTransaction] = useState(true);
  const [isRunning, setIsRunning] = useState(false);
  const [error, setError] = useState("");

  // The allowlisted databases this environment is actually configured to reach. The Logging database is never
  // among them: it holds the record of these very runs.
  const targetsQuery = useQuery(["dataToolsService.getScriptTargets"], () =>
    dataToolsService.getScriptTargets(),
  );

  const targets = targetsQuery.data?.data ?? [];

  const handleRun = async () => {
    if (!target || !script.trim()) {
      return;
    }
    setIsRunning(true);
    setError("");
    try {
      const response = await dataToolsService.runTool(
        SCRIPT_RUNNER_TOOL_KEY,
        new RunToolRequest({
          input: JSON.stringify({
            targetDatabase: target,
            script,
            useTransaction,
          }),
        }),
      );
      if (response.data?.id) {
        setScript("");
        navigate(dataToolsRunUrl(response.data.id));
        return;
      }
      setError(response.responseReason ?? t({ id: "scriptRunnerRunRefused" }));
    } catch {
      // A rejected request is not proof that nothing ran: the job may already have been queued when the
      // response was lost. Say so rather than leaving the operator with a silently re-enabled button.
      setError(t({ id: "scriptRunnerRunUnknown" }));
    } finally {
      setIsRunning(false);
    }
  };

  return (
    <Container>
      <Box mb={4}>
        <Typography variant="h5" gutterBottom>
          {t({ id: "scriptRunner" })}
        </Typography>

        <Alert severity="warning" sx={styles.warning}>
          {t({ id: "scriptRunnerWarning" })}
        </Alert>

        {error && (
          <Alert severity="error" sx={styles.warning}>
            {error}
          </Alert>
        )}

        <TextField
          select
          fullWidth
          label={t({ id: "targetDatabase" })}
          value={target}
          onChange={(e) => setTarget(e.target.value)}
          disabled={targetsQuery.isFetching}
        >
          {targets.map((name) => (
            <MenuItem key={name} value={name}>
              {name}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          fullWidth
          multiline
          minRows={8}
          label={t({ id: "sqlScript" })}
          value={script}
          onChange={(e) => setScript(e.target.value)}
          sx={styles.script}
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={useTransaction}
              onChange={(e) => setUseTransaction(e.target.checked)}
            />
          }
          label={t({ id: "wrapInTransaction" })}
        />

        {!useTransaction && (
          <Alert severity="info" sx={styles.transactionHint}>
            {t({ id: "noTransactionHint" })}
          </Alert>
        )}

        <Box display="flex" justifyContent="flex-end">
          <Button
            variant="contained"
            color="error"
            onClick={handleRun}
            disabled={isRunning || !target || !script.trim()}
            startIcon={
              isRunning ? (
                <CircularProgress size={16} color="inherit" />
              ) : undefined
            }
          >
            {t({ id: "run" })}
          </Button>
        </Box>
      </Box>

      <Alert severity="info">{t({ id: "scriptRunnerHistoryHint" })}</Alert>
    </Container>
  );
};

export default ScriptRunnerList;
